import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { LIGHT_TOKENS, DARK_TOKENS, type TokenSet } from "./tokens";

type ThemeName = "light" | "dark";

/**
 * Resolve the active theme. Precedence: explicit override > host
 * prefers-color-scheme > "light". Subscribes to runtime scheme changes.
 * Returns "light" when matchMedia is unavailable (old browsers / SSR / jsdom).
 */
export function useResolvedTheme(override?: ThemeName): ThemeName {
  const read = (): ThemeName => {
    if (override) return override;
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  };
  const [theme, setTheme] = useState<ThemeName>(read);

  useEffect(() => {
    if (override) { setTheme(override); return; }
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") { setTheme("light"); return; }
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    setTheme(mql.matches ? "dark" : "light");
    const onChange = (e: { matches: boolean }) => setTheme(e.matches ? "dark" : "light");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [override]);

  return theme;
}

const TokensContext = createContext<TokenSet | null>(null);

export function EmbedThemeProvider({ themeOverride, children }: { themeOverride?: ThemeName; children: ReactNode }) {
  const theme = useResolvedTheme(themeOverride);
  const tokens = theme === "dark" ? DARK_TOKENS : LIGHT_TOKENS;
  return <TokensContext.Provider value={tokens}>{children}</TokensContext.Provider>;
}

/** Active token set. Throws if used outside an EmbedThemeProvider. */
export function useEmbedTokens(): TokenSet {
  const t = useContext(TokensContext);
  if (!t) throw new Error("useEmbedTokens must be used within an EmbedThemeProvider");
  return t;
}
